import type { ReactNode } from 'react';

/**
 * Single-page layout for all unauthenticated pages.
 *
 * Background_dreamIT.png fills the viewport; a centered card carries the
 * form. The official logo sits above the card; the footer (copyright +
 * legal links) sits below. No left/right split — the same layout renders
 * at every breakpoint.
 */
export function AuthShell({
  title,
  lede,
  children,
}: {
  title: ReactNode;
  lede: ReactNode;
  children: ReactNode;
}) {
  // `w-full`, not `w-screen`: 100vw includes the scrollbar gutter, which put a
  // horizontal scrollbar on every narrow viewport.
  // `100svh` is the SMALL viewport height — on mobile Safari/Chrome the URL bar
  // collapses on scroll, and plain `100vh` left the footer cut off underneath it.
  return (
    <div
      className="relative min-h-screen min-h-[100svh] w-full overflow-x-hidden overflow-y-auto font-sans bg-app flex flex-col"
      style={{
        // Notched phones in landscape: keep content clear of the cutout.
        // Set on the root, which carries no padding of its own, so it
        // composes with the inner container's responsive padding rather
        // than overriding it.
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      {/* Full-viewport background. The composition has the heading +
          tagline rendered into the artwork already, so we don't overlay
          HTML text — the card sits on top as the focal point. */}
      <img
        src="/Background_dreamIT.png"
        alt=""
        aria-hidden
        className="pointer-events-none fixed inset-0 w-full h-full object-cover"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
      />
      {/* Soft wash to keep the centered card legible regardless of which
          part of the artwork ends up behind it. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0"
        style={{ background: 'rgba(255, 255, 255, 0.55)' }}
      />

      {/* sr-only — visible heading lives in the artwork, but assistive
          tech still needs a page identity. */}
      <h1 className="sr-only">{title}</h1>
      <p className="sr-only">{lede}</p>

      {/* Right-aligned column — leaves the artwork's left-side heading
          ("Clinical Trial Management System") visible on the left half
          of the viewport; the form sits over the laptop/dashboard
          portion of the imagery. The logo + form share a single 460px
          container so the logo is centered relative to the form card,
          not pinned to the right edge of the column. */}
      <div className="relative z-10 flex-1 flex flex-col items-center lg:items-end justify-center px-4 sm:px-6 lg:pl-4 lg:pr-24 py-8 sm:py-10">
        <div className="w-full max-w-[460px]">
          {/* Logo — centered above the form card. Scales down on phones so
              it doesn't eat the vertical space the form needs. */}
          <div className="flex justify-center mb-5 sm:mb-6">
            <img
              src="/DreamIT_logo.png"
              alt="DreamIT"
              className="h-14 sm:h-16 lg:h-20 w-auto drop-shadow-sm"
            />
          </div>

          {/* Form card */}
          {children}
        </div>
      </div>

      {/* Footer — pinned to the bottom of the viewport and centered
          horizontally across the full page, so the legal links don't
          chase the right-aligned form column. */}
      <div
        className="relative z-10 shrink-0 flex flex-col items-center gap-1.5 text-[10.5px] text-text-3 px-4 pt-4 pb-5"
        style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
      >
        <div className="flex flex-wrap justify-center gap-2 text-text-4">
          {([
            { label: 'Privacy Policy', href: '/legal/privacy' },
            { label: 'Terms & Conditions', href: '/legal/terms' },
            { label: 'Cookies', href: '/legal/cookies' },
          ] as const).map((item, i, arr) => (
            <span key={item.label} className="flex items-center gap-2">
              <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-text-3 underline decoration-transparent hover:decoration-current"
              >
                {item.label}
              </a>
              {i < arr.length - 1 && <span className="text-border-strong">·</span>}
            </span>
          ))}
        </div>
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-text-4 text-center">
          <span>© 2026 DreamTrials · All rights reserved</span>
          <span aria-hidden className="hidden sm:inline">·</span>
          <span>v1.0.0</span>
        </div>
      </div>
    </div>
  );
}

/** HIPAA pill rendered absolute inside a form card. */
export function HipaaBadge() {
  return (
    <div className="absolute top-3 right-3.5 inline-flex items-center gap-1 bg-navy-50 border border-navy/15 rounded-full pl-1.5 pr-2 py-0.5 text-[9px] font-bold text-navy uppercase tracking-wider">
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        <polyline points="9 12 11 14 15 10"/>
      </svg>
      HIPAA Compliant
    </div>
  );
}

/** Standard error banner used inside form cards. */
export function AuthErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 bg-critical-soft border border-critical/30 rounded-sm px-3 py-2.5 text-[11px] text-critical">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      <span>{message}</span>
    </div>
  );
}
